<template>
  <div class="space-y-6 p-4 border rounded-md shadow-sm">
    
    <!-- Dwelling Coverage -->
    <div class="space-y-3">
      <h4 class="text-md font-semibold text-center">
        Adjust Dwelling Coverage
      </h4>
      <p class="text-center text-xs text-gray-600">
        Coverage A: Protection for your home\'s structure. Select $10,000 to $2,000,000.
      </p>
      <div class="flex flex-col items-center justify-center gap-2">
        <div class="text-center text-xl font-medium text-gray-700">
          ${{ (modelValue.dwellingAmount || 0).toLocaleString() }}
        </div>
        <Slider
          :model-value="modelValue.dwellingAmount || 0"
          @update:modelValue="updateDetail('dwellingAmount', $event)"
          :min="10000"
          :max="2000000"
          :step="10000"
          class="w-full md:w-3/4"
          aria-labelledby="dwelling-coverage-label"
        />
        <span id="dwelling-coverage-label" class="sr-only">Dwelling Coverage Amount Slider</span>
      </div>
    </div>

    <!-- Personal Property Coverage -->
     <div class="space-y-3 border-t pt-4 mt-4">
      <h4 class="text-md font-semibold text-center">
        Adjust Personal Property Coverage
      </h4>
      <p class="text-center text-xs text-gray-600">
        Coverage C: Protection for your belongings. Select $10,000 to $2,000,000.
      </p>
      <div class="flex flex-col items-center justify-center gap-2">
        <div class="text-center text-xl font-medium text-gray-700">
            ${{ (modelValue.personalPropertyAmount || 0).toLocaleString() }}
        </div>
        <Slider
            :model-value="modelValue.personalPropertyAmount || 0"
            @update:modelValue="updateDetail('personalPropertyAmount', $event)"
            :min="10000"
            :max="2000000"
            :step="10000"
            class="w-full md:w-3/4"
             aria-labelledby="personal-property-label"
        />
         <span id="personal-property-label" class="sr-only">Personal Property Coverage Amount Slider</span>
      </div>
    </div>

    <!-- Deductible -->
    <div class="space-y-3 border-t pt-4 mt-4">
      <h4 class="text-md font-semibold text-center">
        Adjust Deductible
      </h4>
      <p class="text-center text-xs text-gray-600">
         Your out-of-pocket cost per claim. Choose $500 to $5,000.
      </p>
      <div class="flex flex-col items-center justify-center gap-2">
        <div class="text-center text-xl font-medium text-gray-700">
          ${{ (modelValue.deductibleAmount || 0).toLocaleString() }}
        </div>
        <Slider
            :model-value="modelValue.deductibleAmount || 0"
            @update:modelValue="updateDetail('deductibleAmount', $event)"
            :min="500"
            :max="5000"
            :step="500"
            class="w-full md:w-3/4"
             aria-labelledby="deductible-label"
        />
        <span id="deductible-label" class="sr-only">Deductible Amount Slider</span>
      </div>
    </div>
    
     <!-- Hidden input for validation trigger if needed, though slider interaction should suffice -->
     <input type="hidden" :value="JSON.stringify(modelValue)" aria-hidden="true" />
     <p v-if="errors.coverageValues" class="error-message text-center">{{ errors.coverageValues }}</p>

  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, computed } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { Slider } from '@/components/ui';

const props = defineProps({
  modelValue: { 
    type: Object,
    required: true,
    default: () => ({ 
      dwellingAmount: 300000, // Default values matching parent form
      personalPropertyAmount: 150000, 
      deductibleAmount: 2000 
    }) 
  }
});

const emit = defineEmits(['update:modelValue']);

// Ensure internal data always has the necessary fields for validation and display
const internalData = computed(() => ({
  dwellingAmount: props.modelValue?.dwellingAmount ?? 300000,
  personalPropertyAmount: props.modelValue?.personalPropertyAmount ?? 150000,
  deductibleAmount: props.modelValue?.deductibleAmount ?? 2000,
}));

const validationSchema = yup.object({
   dwellingAmount: yup.number().required().min(10000).max(2000000),
   personalPropertyAmount: yup.number().required().min(10000).max(2000000),
   deductibleAmount: yup.number().required().min(500).max(5000),
});

const { errors, validate: validateInternally, setValues, meta } = useForm({
  validationSchema: validationSchema,
  initialValues: internalData.value // Use computed internalData for reliable initialization
});

watch(internalData, (newData) => {
  setValues(newData);
}, { deep: true, immediate: true }); // immediate to set initial values correctly


const updateDetail = (key, value) => {
  const newValue = { 
    ...props.modelValue, // Spread the original modelValue to preserve other potential fields
    [key]: value 
  };
  // Ensure all core fields are present when emitting, even if just taking from internalData defaults
  const comprehensiveValue = {
      dwellingAmount: newValue.dwellingAmount ?? internalData.value.dwellingAmount,
      personalPropertyAmount: newValue.personalPropertyAmount ?? internalData.value.personalPropertyAmount,
      deductibleAmount: newValue.deductibleAmount ?? internalData.value.deductibleAmount,
      ...newValue // ensure any other fields on newValue are preserved
  };
  emit('update:modelValue', comprehensiveValue);
};

// --- Expose validation function to parent ---
defineExpose({
  async validate() {
    // Ensure vee-validate has the most current data before validating
    setValues(internalData.value); 
    const result = await validateInternally();
    return result.valid;
  }
});
</script>

<style scoped>
.error-message {
    @apply text-red-500 text-xs italic mt-1;
}
/* Add any specific styles if needed */
</style> 